import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { reorderVideoImages } from '../../services/videoImageApi';
import { SnackbarType } from '../Snackbar/types';
import { VideoEditImagesSectionProps, VideoImage, UseVideoEditImagesSectionReturn } from './types';
import { SessionManager } from '../../utils/SessionManager';

/**
 * VideoEditImagesSection のビジネスロジックと副作用を管理するカスタムフック
 */
export const useVideoEditImagesSection = (
  props: VideoEditImagesSectionProps
): UseVideoEditImagesSectionReturn => {
  const { video, videoImages, allImages, handleDeleteImage, handleImageFileChange, onReorderModeChange, onTempImagesChange, onCommitPendingDeletionsChange, onCommitTempImagesChange, onImageOrderChange } = props;
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const showSnackbar = useSnackbar();
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  
  // モバイル画像並べ替えモード関連の状態
  const [isReorderMode, setIsReorderMode] = useState<boolean>(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  
  // 一時的な画像管理
  const [tempImages, setTempImages] = useState<VideoImage[]>([]);
  
  const [isUploading, setIsUploading] = useState<boolean>(false);
  
  // 削除予定の画像管理
  const [pendingDeletions, setPendingDeletions] = useState<string[]>([]);
  
  // 画像順序の状態管理（ドラッグ&ドロップ時の一時的な順序変更用）
  const [localOrderedImages, setLocalOrderedImages] = useState<VideoImage[]>([]);
  
  // 複数ファイルアップロード関連
  const MAX_UPLOAD_COUNT = 5; // TODO: 環境変数から動的設定

  // 画像順序の計算
  // 新しいフローでは、videoImages（convertedVideoImages）にすべての画像が含まれている
  const orderedImages = useMemo(() => {
    // ローカルの順序が設定されている場合はそれを使用
    if (localOrderedImages.length > 0) {
      return localOrderedImages;
    }
    
    // videoImages をそのまま使用（既に file プロパティ付きで変換済み）
    if (videoImages && videoImages.length > 0) {
      const sortedImages = [...videoImages]
        .sort((a, b) => {
          if (a.display_order === undefined || b.display_order === undefined) {
            const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
            const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
            return aTime - bTime;
          }
          return a.display_order - b.display_order;
        });
      
      
      return sortedImages;
    }
    
    return [];
  }, [videoImages, localOrderedImages]);
  
  // 削除予定を除いた既存画像・保存予定の画像の数を計算
  const activeImageCount = useMemo(() => {
    const activeImages = orderedImages.filter(img => {
      // 削除予定でない画像のみをカウント
      const isPendingDeletion = props.isPendingDeletion ? props.isPendingDeletion(img.id) : false;
      return !isPendingDeletion;
    });
    
    return activeImages.length;
  }, [orderedImages, props.isPendingDeletion, pendingDeletions]);
  
  // 一時保存画像のIDセットを作成（判定用）
  // 新しいフローでは、temp_ で始まるIDを持つ画像を一時画像として扱う
  const tempImageIds = useMemo(() => {
    const tempIds = orderedImages
      .filter(img => img.id.startsWith('temp_'))
      .map(img => img.id);
    return new Set(tempIds);
  }, [orderedImages]);

  // レスポンシブ対応
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        // PCサイズになったら並べ替えモードを解除
        setIsReorderMode(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // 並べ替えモード終了時の状態クリア
  useEffect(() => {
    if (!isReorderMode) {
      // 状態が変更された場合のみ更新
      setSelectedImages(prev => prev.length > 0 ? [] : prev);
      setIsProcessing(prev => prev ? false : prev);
      setLocalOrderedImages(prev => prev.length > 0 ? [] : prev);
    }
  }, [isReorderMode]);

  // 親コンポーネントに並べ替えモードの状態変更を通知（別のuseEffect）
  useEffect(() => {
    if (onReorderModeChange) {
      onReorderModeChange(isReorderMode);
    }
  }, [isReorderMode]);

  // 画像URLを取得する関数
  const getImageUrl = (image: VideoImage): string => {
    // ① imageがない場合はデフォルト画像
    if (!image) {
      return '/images/no-image.jpg';
    }

    // ② 一時保存画像の場合（Fileオブジェクトが存在する場合）
    if ((image as any).file && (image as any).file instanceof File) {
      try {
        const objectUrl = URL.createObjectURL((image as any).file);
        return objectUrl;
      } catch (error) {
        return '/images/no-image.jpg';
      }
    }

    // ③ image.urlがhttp(s)://で始まる場合は素通し（外部URL）
    if (image.url && (image.url.startsWith('http://') || image.url.startsWith('https://'))) {
      return image.url;
    }

    // ④ image.urlがある場合は先頭スラッシュ正規化して相対URL化
    if (image.url) {
      return '/' + image.url.replace(/^\/+/, '');
    }

    // ③ image.image_pathがhttp(s)://で始まる場合は素通し（外部URL）
    if (image.image_path && (image.image_path.startsWith('http://') || image.image_path.startsWith('https://'))) {
      return image.image_path;
    }

    // ④ image.image_pathがある場合は先頭スラッシュ正規化して相対URL化
    if (image.image_path) {
      return '/' + image.image_path.replace(/^\/+/, '');
    }

    // ⑤ どちらもない場合はデフォルト画像
    return '/images/no-image.jpg';
  };

  // 一時的な画像アップロード処理
  const handleTempImageUpload = useCallback(async (file: File): Promise<void> => {
    try {
      setIsUploading(true);
      
      // ファイルをFormDataに変換
      const formData = new FormData();
      formData.append('image', file);
      
      // 認証トークンを取得
      const activeSession = SessionManager.getActiveSession();
      const token = activeSession ? activeSession.token : null;
      
      if (!token) {
        throw new Error('認証トークンが見つかりません。ログインし直してください。');
      }

      // 一時的な画像アップロードAPIを呼び出し
      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
      };
      
      const response = await fetch('/api/videos/temp-images', {
        method: 'POST',
        headers: requestHeaders,
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
    
        console.error('APIエラーレスポンス:', errorText);
        throw new Error(`画像のアップロードに失敗しました: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      // 一時的な画像として保存
      const tempImage: VideoImage = {
        id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        video_id: video.id,
        url: result.url,
        image_path: result.image_path,
        display_order: (videoImages?.length || 0) + tempImages.length + 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_temp: true // 一時的な画像であることを示すフラグ
      };
      
      setTempImages(prev => [...prev, tempImage]);
      showSnackbar('画像をアップロードしました（保存時に正式に紐付けされます）', SnackbarType.SUCCESS);
      
    } catch (error) {
      console.error('一時的な画像アップロードエラー:', error);
      const errorMessage = error instanceof Error ? error.message : '画像のアップロードに失敗しました';
      showSnackbar(errorMessage, SnackbarType.ERROR);
    } finally {
      setIsUploading(false);
    }
  }, [video.id, videoImages?.length, tempImages.length, showSnackbar]);

  // 複数ファイルの処理
  const handleMultipleFiles = (files: FileList): void => {
    // 新しいフローでは handleImageFileChange を使用するため、この関数は非推奨
    console.warn('handleMultipleFiles は非推奨です。handleImageFileChange を使用してください。');
    if (!files || files.length === 0) {
      return;
    }
    handleImageFileChange(files);
  };

  // ドラッグ&ドロップ処理（ファイルアップロード用）
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (): void => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length === 0) return;

    // 新しいフローでは handleImageFileChange を使用
    const payload = files.length === 1 ? files[0] : files;
    handleImageFileChange(payload);
  };

  // 画像ファイル変更処理（新しいフロー）
  const handleImageFileChangeWrapper = async (
    fileOrEvent: React.ChangeEvent<HTMLInputElement> | File | FileList | File[]
  ) => {
    await handleImageFileChange(fileOrEvent);
  };

  const handleFileButtonClick = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // PC用ドラッグ&ドロップ処理（画像並べ替え用）
  const handleImageDragStart = (e: React.DragEvent<HTMLDivElement>, index: number): void => {
    e.dataTransfer.setData('text/plain', String(index));
    e.currentTarget.style.opacity = '0.6';
  };

  const handleImageDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('dragover');
  };

  const handleImageDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.currentTarget.classList.remove('dragover');
  };

  const handleImageDragEnd = (e: React.DragEvent<HTMLDivElement>): void => {
    e.currentTarget.style.opacity = '1';
    e.currentTarget.classList.remove('dragover');
  };

  const handleImageDrop = async (e: React.DragEvent<HTMLDivElement>, destinationIndex: number): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    
    e.currentTarget.classList.remove('dragover');
    
    const sourceIndexStr = e.dataTransfer.getData('text/plain');
    
    if (!sourceIndexStr) {
      console.error('ドラッグ元のインデックスが取得できません');
      showSnackbar("画像の順序更新に失敗しました", SnackbarType.ERROR);
      return;
    }
    
    const sourceIndex = parseInt(sourceIndexStr, 10);
    
    if (isNaN(sourceIndex)) {
      console.error(`不正なソースインデックス: "${sourceIndexStr}"`);
      showSnackbar("画像の順序更新に失敗しました", SnackbarType.ERROR);
      return;
    }
    
    if (sourceIndex === destinationIndex) return;
    
    try {
      // APIの呼び出し前に新しい配列を作成
      const newOrderedImages = [...orderedImages];
      
      // 配列内で要素を移動（sourceから取り出してdestinationに挿入）
      const [movedItem] = newOrderedImages.splice(sourceIndex, 1);
      newOrderedImages.splice(destinationIndex, 0, movedItem);
      
      // 表示順序を一時的に更新
      setLocalOrderedImages(newOrderedImages);
      
      // 並び順をAPIに送信（すべての画像のIDを順番通りに送信）
      const imageIds = newOrderedImages.map(img => img.id);
      
      // API呼び出し
      const result = await reorderVideoImages(video.id, imageIds);
      
      // 成功判定: result.successがtrue、またはresult.messageが存在する場合
      if (result.success === true || result.message) {
        // 成功通知
        showSnackbar("画像の順序を更新しました", SnackbarType.SUCCESS);
        
        // 親コンポーネントに新しい順序を通知
        if (onImageOrderChange) {
          onImageOrderChange(newOrderedImages);
        }
        
        // 成功時はローカルの順序を保持し続ける（親コンポーネントからの更新を待つ）
        // 親コンポーネントが新しい順序でvideoImagesを更新したら、useMemoが自動的に更新される
      } else {
        throw new Error('API呼び出しが失敗しました');
      }
    } catch (error) {
      console.error('画像順序の更新エラー:', error);
      showSnackbar("画像の順序更新に失敗しました", SnackbarType.ERROR);
      
      // エラー時はローカルの順序をクリアして元の状態に戻す
      setLocalOrderedImages([]);
    }
  };

  // モバイル用画像並べ替え機能
  const toggleReorderMode = (): void => {
    const newMode = !isReorderMode;
    setIsReorderMode(newMode);
    setSelectedImages([]);
    // 親コンポーネントへの通知はuseEffectで行う
  };
  
  // 画像選択処理
  const handleImageSelect = (image: VideoImage): void => {
    if (isProcessing) return;
    
    setSelectedImages(prevSelected => {
      // すでに選択されている場合、選択から削除
      if (prevSelected.includes(image.id)) {
        return prevSelected.filter(id => id !== image.id);
      }
      // 選択されていない場合、選択に追加
      return [...prevSelected, image.id];
    });
  };
  
  // 選択順序に基づいて画像を並べ替える
  const applySelectedOrder = async (): Promise<void> => {
    if (selectedImages.length === 0) {
      showSnackbar("画像が選択されていません", SnackbarType.WARNING);
      return;
    }
    
    if (selectedImages.length !== orderedImages.length) {
      showSnackbar("すべての画像を選択してください", SnackbarType.WARNING);
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // 選択された順序で画像を並べ替え
      const newOrderedImages = selectedImages.map(id => 
        orderedImages.find(img => img.id === id)
      ).filter((img): img is VideoImage => img !== undefined);
      
      // 一時的に新しい順序を反映
      setLocalOrderedImages(newOrderedImages);
      
      // APIに新しい順序を送信
      const result = await reorderVideoImages(video.id, selectedImages);
      
      // 成功通知
      showSnackbar("画像の順序を更新しました", SnackbarType.SUCCESS);
      
      // ローカルの順序をクリア（親コンポーネントから新しい順序が来るまで）
      setLocalOrderedImages(prev => prev.length > 0 ? [] : prev);
      
      // 並べ替えモードを終了
      setIsReorderMode(false);
      setSelectedImages([]);
    } catch (error) {
      console.error('画像順序の更新エラー:', error);
      showSnackbar("画像の順序更新に失敗しました", SnackbarType.ERROR);
      
      // エラー時はローカルの順序をクリアして元の状態に戻す
      setLocalOrderedImages([]);
    } finally {
      setIsProcessing(false);
    }
  };

  // 並べ替えモードをキャンセル
  const cancelReorderMode = (): void => {
    setIsReorderMode(false);
    setSelectedImages([]);
    // localOrderedImagesのクリアは条件付きで行う
    setLocalOrderedImages(prev => prev.length > 0 ? [] : prev);
  };

  // 画像の選択状態を取得
  const getSelectionStatus = (imageId: string): number | null => {
    if (!selectedImages.includes(imageId)) {
      return null;
    }
    return selectedImages.indexOf(imageId) + 1;
  };

  // 画像の削除処理（保存まで保留）- 新しいフローでは使用しない
  const handleTempImageDelete = useCallback(async (imageId: string): Promise<void> => {
    // 新しいフローでは、この関数は使用されない
    // toggleDeletionStatus が代わりに使用される
    console.warn('handleTempImageDelete は非推奨です。toggleDeletionStatus を使用してください。');
  }, []);

  // 一時的な画像を正式に紐付ける処理（新しいフローでは使用しない）
  const commitTempImages = useCallback(async (): Promise<VideoImage[]> => {
    // 新しいフローでは、この関数は使用されない
    // useVideoEditImages の saveImageChanges で処理される
    console.warn('commitTempImages は非推奨です。useVideoEditImages の saveImageChanges を使用してください。');
    return videoImages || [];
  }, [videoImages]);

  // 削除予定の画像を実際に削除する処理（新しいフローでは使用しない）
  const commitPendingDeletions = useCallback(async (): Promise<void> => {
    // 新しいフローでは、削除予定の画像は useVideoEditImages フックの saveImageChanges で処理される
    // この関数は後方互換性のために残しているが、実際には呼び出されない
    console.warn('commitPendingDeletions は非推奨です。useVideoEditImages の saveImageChanges を使用してください。');
  }, []);

  // 一時的な画像を削除する処理（保存されなかった場合）
  const cleanupTempImages = useCallback(async (): Promise<void> => {
    if (tempImages.length === 0) {
      return;
    }

    try {
      // TODO: クリーンアップ処理のエラーハンドリング強化
      // TODO: 部分的な削除失敗時の処理
      // すべての一時的な画像を削除
      await Promise.all(
        tempImages.map(async (tempImage) => {
          try {
            await fetch(`/api/videos/temp-images/${tempImage.id}`, {
              method: 'DELETE',
            });
          } catch (error) {
        
            console.error(`一時的な画像削除エラー (${tempImage.id}):`, error);
          }
        })
      );

      // ローカル状態をクリア
      setTempImages([]);
      setPendingDeletions([]);
    } catch (error) {
  
      console.error('一時的な画像クリーンアップエラー:', error);
    }
  }, [tempImages]);

  // コールバック関数を親に渡す（useEffectを使わずに直接実行）
  // ただし、関数が変更された場合のみ実行
  const prevCommitPendingDeletions = useRef<typeof commitPendingDeletions | undefined>(undefined);
  return {
    isDragging,
    fileInputRef,
    isMobile,
    orderedImages,
    isReorderMode,
    selectedImages,
    isProcessing,
    isUploading,
    tempImages,
    tempImageIds,
    pendingDeletions,
    MAX_UPLOAD_COUNT,
    activeImageCount,
    getImageUrl,
    handleMultipleFiles,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileButtonClick,
    handleImageDragStart,
    handleImageDragOver,
    handleImageDragLeave,
    handleImageDragEnd,
    handleImageDrop,
    toggleReorderMode,
    handleImageSelect,
    applySelectedOrder,
    cancelReorderMode,
    getSelectionStatus,
    handleTempImageDelete,
    commitTempImages,
    commitPendingDeletions,
    cleanupTempImages,
    handleImageFileChange: handleImageFileChangeWrapper,
    setLocalOrderedImages
  };
}; 
